import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { SPECIALTIES, CITIES, INSURANCES, CONDITIONS } from "@/data/heallexa";
import { MapPin } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { specialtySlug, citySlug, stateFromNameSlug, stateNameSlug, toSlug } from "@/lib/seo-slugs";

export const Route = createFileRoute("/browse")({
 head: () => ({
 meta: [
 { title: "Browse Doctors, Dentists & Clinics by Specialty, Heallexa" },
 { name: "description", content: "Browse providers by specialty, city, insurance, and condition. Doctors, dentists, therapists, chiropractors, and urgent care on Heallexa." },
 { property: "og:title", content: "Browse Doctors, Dentists & Clinics by Specialty, Heallexa" },
 { property: "og:description", content: "Find healthcare providers by specialty, city, insurance, and condition on the Heallexa directory." },
 { property: "og:url", content: "https://heallexa.com/browse" },
 { property: "og:type", content: "website" },
 ],
 links: [{ rel: "canonical", href: "https://heallexa.com/browse" }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: "Browse Doctors, Dentists & Clinics by Specialty",
 description: "Browse healthcare providers on Heallexa by specialty, city, insurance, and condition.",
 url: "https://heallexa.com/browse",
 isPartOf: { "@type": "WebSite", name: "Heallexa", url: "https://heallexa.com" },
 about: [
 { "@type": "Thing", name: "Specialties" },
 { "@type": "Thing", name: "Cities" },
 { "@type": "Thing", name: "Insurance" },
 { "@type": "Thing", name: "Conditions" },
 ],
 }),
 },
 ],
 }),
 component: BrowsePage,
});

function BrowsePage() {
 const { t } = useI18n();
 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />
 <header className="pt-32 md:pt-24 pb-12 border-b border-border bg-card">
 <div className="mx-auto max-w-[1200px] px-6">
 <Reveal from="up">
 <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
 Heallexa directory
 </p>
 <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
 {t.browse.title}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">{t.browse.subtitle}</p>
 </Reveal>
 </div>
 </header>

 <main className="mx-auto max-w-[1200px] px-6 py-16 space-y-16">
 <Section title={t.browse.bySpec}>
 {SPECIALTIES.map((s) => (
 <LinkItem key={s} href={`/specialties/${specialtySlug(s)}`}>{s}</LinkItem>
 ))}
 </Section>

 <Section title={t.browse.byCity}>
 {CITIES.map((c) => {
 const [, stateCode] = c.split(", ");
 const stateRec = stateFromNameSlug(stateCode ?? "");
 const stSlug = stateRec ? stateNameSlug(stateRec.name) : (stateCode ?? "").toLowerCase();
 return (
 <LinkItem key={c} href={`/locations/${stSlug}/${citySlug(c)}`} icon>
 {c}
 </LinkItem>
 );
 })}
 </Section>

 <Section title={t.browse.byIns}>
 {INSURANCES.map((i) => (
 <LinkItem key={i.id} href={`/insurance/${i.id}`} accent={i.color}>{i.short}</LinkItem>
 ))}
 </Section>

 <Section title={t.browse.byCond}>
 {CONDITIONS.map((c) => (
 <LinkItem key={c} href={`/conditions/${toSlug(c)}`}>{c}</LinkItem>
 ))}
 </Section>
 </main>

 <FooterSection />
 </div>
 );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
 return (
 <Reveal as="section" from="up" delay={60}>
 <h2 className="font-display text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
 {title}
 </h2>
 <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-3">
 {children}
 </ul>
 </Reveal>
 );
}

function LinkItem({
 href,
 children,
 icon,
 accent,
}: {
 href: string;
 children: React.ReactNode;
 icon?: boolean;
 accent?: string;
}) {
 return (
 <li>
 <a
 href={href}
 className="inline-flex items-center gap-2 text-[14px] text-muted-foreground hover:text-primary transition-colors"
 >
 {icon ? (
 <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
 ) : accent ? (
 <span
 className="h-2.5 w-2.5 shrink-0 rounded-sm"
 style={{ backgroundColor: accent }}
 aria-hidden
 />
 ) : null}
 <span className="truncate">{children}</span>
 </a>
 </li>
 );
}
